// Centralized Chart Styles Configuration
// This file consolidates all BBC-style chart configurations used across the application

export const BBC_STYLE = {
  // Typography
  FONT_FAMILY: "'DM Sans', sans-serif",
  TITLE_SIZE: '1.5rem',
  SUBTITLE_SIZE: '1rem',
  AXIS_SIZE: 11,
  SOURCE_SIZE: '0.75rem',

  // Primary Colors
  PRIMARY_COLOR: '#1380A1',     // BBC blue
  SECONDARY_COLOR: '#FAAB18',   // BBC yellow/orange
  HIGHLIGHT_COLOR: '#D55E00',   // Orange for emphasis
  TOP_BRAND_COLOR: '#FAAB18',   // Gold for top brand

  // Semantic Colors
  POSITIVE_COLOR: '#2E7D32',    // Green for positive values
  NEGATIVE_COLOR: '#ef4444',    // Red for negative values
  NEUTRAL_COLOR: '#9e9e9e',     // Gray for neutral

  // Text Colors
  TEXT_COLOR: '#333333',
  LIGHT_TEXT: '#666666',
  SOURCE_COLOR: '#666666',

  // UI Colors
  AXIS_COLOR: '#B0B0B0',
  BORDER_COLOR: '#E5E7EB',
  DIVIDER_COLOR: '#D0D0D0',
  WHITE: '#ffffff',

  // Chart Margins
  MARGIN: {
    top: 40,
    right: 30,
    left: 20,
    bottom: 20
  },
  MARGIN_COMPACT: {
    top: 20,
    right: 30,
    left: 20,
    bottom: 20
  },

  // Common Spacing
  SPACING: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem'
  },

  // Border Radius
  RADIUS: {
    sm: '2px',
    md: '3px',
    lg: '4px',
    xl: '8px'
  },

  // Common Transitions
  TRANSITION: {
    fast: '150ms ease',
    base: '250ms ease',
    slow: '350ms ease'
  }
};

// Chart color palettes for different chart types
export const CHART_COLORS = {
  // For pie charts, treemaps, etc.
  CATEGORICAL: [
    '#FF6B6B', '#39A189', '#3497BF', '#79AB93', '#CFB43D',
    '#B57777', '#8087B3', '#77A88A', '#CC756B', '#A1AB89',
    '#95A3D6', '#CC8F69', '#959595', '#C279C2', '#A594E3',
    '#4CAF50', '#9C27B0', '#FF9800', '#2196F3', '#E91E63',
    '#00BCD4', '#FFC107', '#607D8B', '#795548', '#3F51B5'
  ],

  // For status/sentiment colors
  SENTIMENT: {
    positive: '#22c55e',
    negative: '#ef4444',
    neutral: '#9e9e9e'
  },

  // For comparison charts
  COMPARISON: {
    primary: BBC_STYLE.PRIMARY_COLOR,
    secondary: BBC_STYLE.SECONDARY_COLOR
  }
};

// Common style functions
export const styleHelpers = {
  // Create a highlighted text span
  highlightText: (text, color = BBC_STYLE.HIGHLIGHT_COLOR, underline = true) => ({
    fontWeight: '700',
    color: color,
    ...(underline && {
      borderBottom: `2px solid ${color}`,
      paddingBottom: '1px'
    })
  }),

  // Create a badge/chip style
  badge: (backgroundColor, color = null) => ({
    fontWeight: '600',
    backgroundColor: backgroundColor || 'rgba(19, 128, 161, 0.08)',
    color: color || 'inherit',
    padding: '2px 6px',
    borderRadius: BBC_STYLE.RADIUS.md,
    display: 'inline-block'
  }),

  // Create a metric dot
  metricDot: (color) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: color,
    display: 'inline-block'
  }),

  // Create a divider
  divider: () => ({
    margin: '0 10px',
    color: BBC_STYLE.DIVIDER_COLOR,
    fontWeight: '300'
  }),

  // Standard title style
  title: (size = BBC_STYLE.TITLE_SIZE) => ({
    fontSize: size,
    fontWeight: '600',
    color: BBC_STYLE.TEXT_COLOR,
    margin: '0 0 12px 0',
    lineHeight: '1.4',
    fontFamily: BBC_STYLE.FONT_FAMILY
  }),

  // Standard subtitle style
  subtitle: (size = BBC_STYLE.SUBTITLE_SIZE) => ({
    fontSize: size,
    color: BBC_STYLE.TEXT_COLOR,
    margin: '0 0 28px 0',
    lineHeight: '1.6',
    fontFamily: BBC_STYLE.FONT_FAMILY
  }),

  // Chart container padding
  chartContainer: () => ({
    fontFamily: BBC_STYLE.FONT_FAMILY,
    padding: '20px'
  }),

  // Responsive chart wrapper
  chartWrapper: () => ({
    width: '100%',
    height: '100%',
    position: 'relative'
  })
};

// Common tooltip styles
export const tooltipStyles = {
  container: {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    border: `1px solid ${BBC_STYLE.BORDER_COLOR}`,
    borderRadius: BBC_STYLE.RADIUS.lg,
    fontSize: '12px',
    padding: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    fontFamily: BBC_STYLE.FONT_FAMILY
  },

  label: {
    color: BBC_STYLE.TEXT_COLOR,
    fontWeight: '600',
    fontSize: '12px',
    marginBottom: '4px'
  },

  value: {
    fontSize: '12px',
    marginTop: '2px'
  }
};

// Common axis styles for charts
export const axisStyles = {
  xAxis: {
    tick: {
      fontSize: BBC_STYLE.AXIS_SIZE,
      fill: BBC_STYLE.AXIS_COLOR,
      fontFamily: BBC_STYLE.FONT_FAMILY
    },
    axisLine: {
      stroke: BBC_STYLE.AXIS_COLOR
    },
    tickLine: false
  },

  yAxis: {
    tick: {
      fontSize: BBC_STYLE.AXIS_SIZE,
      fill: BBC_STYLE.AXIS_COLOR,
      fontFamily: BBC_STYLE.FONT_FAMILY
    },
    axisLine: false,
    tickLine: false
  },

  label: (text, angle = -90) => ({
    value: text,
    angle: angle,
    position: 'insideLeft',
    style: {
      fontSize: BBC_STYLE.AXIS_SIZE,
      fill: BBC_STYLE.AXIS_COLOR,
      textAnchor: 'middle',
      fontFamily: BBC_STYLE.FONT_FAMILY
    }
  })
};

// Export all configurations as a single object for convenience
const chartStyles = {
  BBC_STYLE,
  CHART_COLORS,
  styleHelpers,
  tooltipStyles,
  axisStyles
};

export default chartStyles;
